/* Repository path: frontend/src/features/estimates/EstimateView.tsx */


import { useEffect } from "react";
import type { MouseEvent, ReactNode } from "react";
import type { EstimatePreview, ForwardContributorBasis, ForwardPlanBlock, ItemKind, MoneyAggregate, PlanItem, PlanningBlock, TimelineBlock } from "../../types";
import { Button } from "../../components/Button";
import { PlanItemCard } from "../../components/PlanItemCard";
import { StatusChip } from "../../components/StatusChip";
import { formatCurrency, formatDate, formatTiming } from "../../utils/format";
import { isValidEstimatePayload, planningBlockIfValid, timelineIfValid, forwardPlanIfValid } from "../../api/estimate";

const SECTIONS: {
  kind: ItemKind;
  id: string;
  heading: string;
  intro: string;
  empty: string;
}[] = [
  {
    kind: "action",
    id: "section-actions",
    heading: "Things to do",
    intro: "Steps that may apply to your situation.",
    empty: "No actions needed right now.",
  },
  {
    kind: "obligation",
    id: "section-payments",
    heading: "Payments you may owe",
    intro: "Costs that may apply. Only the estimated ones are added to your monthly reserve.",
    empty: "No payments identified from your answers.",
  },
  {
    kind: "benefit",
    id: "section-benefits",
    heading: "Money you may receive",
    intro: "Support you may be able to claim. We never subtract these from what you owe.",
    empty: "No benefits identified from your answers.",
  },
  {
    kind: "warning",
    id: "section-checks",
    heading: "Things to check",
    intro: "Situations worth understanding before they affect you.",
    empty: "Nothing to flag from your answers.",
  },
];

function followPlanContentsLink(event: MouseEvent<HTMLAnchorElement>) {
  if (
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
  ) return;

  const targetId = decodeURIComponent(event.currentTarget.hash.slice(1));
  const target = document.getElementById(targetId);
  if (!target) return;

  event.preventDefault();
  window.history.pushState({}, "", event.currentTarget.href);
  target.focus({ preventScroll: true });
  target.scrollIntoView?.({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    block: "start",
  });
}

function formatAmountOrRange(
  value: { amount?: string; min?: string; max?: string },
  currency = "EUR",
) {
  return value.amount
    ? formatCurrency(value.amount, currency)
    : `${formatCurrency(value.min as string, currency)}–${formatCurrency(value.max as string, currency)}`;
}

function OneTimeAllowances({
  estimate,
  itemsByCode,
}: {
  estimate: EstimatePreview;
  itemsByCode: ReadonlyMap<string, PlanItem>;
}) {
  const planning = planningBlockIfValid(estimate);
  if (!planning || planning.one_time_allowances.length === 0) return null;
  return (
    <div className="calculation__onetime">
      <p className="eyebrow">One-off costs to plan for separately</p>
      <ul>
        {planning.one_time_allowances.map((row) => (
          <li key={row.item_code}>
            {itemsByCode.get(row.item_code)?.english_name ?? row.item_code}
            {": "}
            {formatAmountOrRange({ min: row.annual_min, max: row.annual_max })}
          </li>
        ))}
      </ul>
    </div>
  );
}

function UnbudgetedRisks({
  estimate,
  itemsByCode,
}: {
  estimate: EstimatePreview;
  itemsByCode: ReadonlyMap<string, PlanItem>;
}) {
  const planning = planningBlockIfValid(estimate);
  if (!planning || planning.unbudgeted_risks.length === 0) return null;
  return (
    <div className="calculation__risks">
      <p className="eyebrow">We can’t responsibly put a number on these yet</p>
      <p>
        These may cost you money, but we don’t have a reviewed range we’d stand behind. Each one
        below tells you where to check.
      </p>
      <ul>
        {planning.unbudgeted_risks.map((row) => (
          <li key={row.item_code}>
            {itemsByCode.get(row.item_code)?.english_name ?? row.item_code}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecommendedBuffer({
  estimate,
  itemsByCode,
}: {
  estimate: EstimatePreview;
  itemsByCode: ReadonlyMap<string, PlanItem>;
}) {
  const contributing = estimate.items.filter((item) => item.included_in_reserve);
  const planning = planningBlockIfValid(estimate);
  const buffer = planning?.recommended_monthly_buffer;
  const allowances = planning?.allowances ?? [];
  const hasAllowances = allowances.length > 0;

  // A €0.00 buffer above a list of real obligations reads as "you owe
  // nothing", which is both false and the exact false certainty this
  // product exists to avoid.
  if (!buffer && contributing.length === 0) {
    return (
      <section className="calculation" aria-labelledby="reserve-title">
        <div>
          <p className="eyebrow">Monthly plan</p>
          <h2 id="reserve-title">We can’t suggest a monthly reserve yet</h2>
          <p>
            We don’t have enough reviewed information to estimate an amount for your situation.
            Everything we found for you is listed below, with what to do about each one.
          </p>
        </div>
        <UnbudgetedRisks estimate={estimate} itemsByCode={itemsByCode} />
      </section>
    );
  }

  // Headline figure: the buffer when one reconciled, otherwise the reserve
  // itself — mathematically the same number whenever there is no allowance
  // yet to add, which today's unsigned, empty policy pack always is. This
  // is also the fallback when `planning` is absent, unreachable, or failed
  // to reconcile: never load-bearing, so the reserve alone still renders
  // exactly as it did before this story existed. See
  // docs/architecture/features/W4-02.md §7.
  const headline = buffer ?? {
    amount: estimate.reserve.monthly,
    min: estimate.reserve.min,
    max: estimate.reserve.max,
  };

  return (
    <section className="calculation" aria-labelledby="reserve-title">
      <div>
        <p className="eyebrow">Monthly plan</p>
        <h2 id="reserve-title">
          Set aside about <span>{formatAmountOrRange(headline)}</span> per month
        </h2>
        <p>
          {hasAllowances
            ? "Part of this is calculated from reviewed official rates. The rest is a conservative planning allowance for costs we can't calculate exactly yet."
            : `We add the monthly equivalents of the ${contributing.length} estimated ${contributing.length === 1 ? "payment" : "payments"} below, then round once to cents using half-up rounding.`}
        </p>
        {hasAllowances && (
          <p className="sr-only">
            This recommendation combines {contributing.length} reviewed{" "}
            {contributing.length === 1 ? "calculation" : "calculations"} with {allowances.length}{" "}
            planning {allowances.length === 1 ? "allowance" : "allowances"}.
          </p>
        )}
      </div>
      <div className="calculation__sum">
        {contributing.map((item) => (
          <p key={item.code}>
            <span>{item.english_name}</span>
            <strong>{item.monthly_equivalent
              ? formatCurrency(item.monthly_equivalent)
              : `${formatCurrency(item.monthly_min_amount as string)}–${formatCurrency(item.monthly_max_amount as string)}`}</strong>
          </p>
        ))}
        {/* W6-01 (D-W6-01-01, R0): no subtotal without a contributor, so an allowance-only
            buffer never shows a "Reviewed calculations € 0.00" row. */}
        {hasAllowances && buffer && contributing.length > 0 && (
          <p className="calculation__subtotal">
            <span>Reviewed calculations</span>
            <strong>{formatAmountOrRange(buffer.composition.calculated_reserve)}</strong>
          </p>
        )}
        {allowances.map((row) => (
          <p key={row.item_code}>
            <span className="calculation__sum-label">
              {itemsByCode.get(row.item_code)?.english_name ?? row.item_code}
              <StatusChip tone="neutral">Planning allowance</StatusChip>
            </span>
            <strong>{formatAmountOrRange({ min: row.monthly_min, max: row.monthly_max })}</strong>
          </p>
        ))}
        {hasAllowances && buffer && (
          <p className="calculation__subtotal">
            <span>Planning allowances</span>
            <strong>{formatAmountOrRange(buffer.composition.planning_allowances)}</strong>
          </p>
        )}
        <p className="calculation__total">
          <span>{hasAllowances ? "Recommended monthly buffer" : "Monthly reserve"}</span>
          <strong>{formatAmountOrRange(headline)}</strong>
        </p>
      </div>
      <OneTimeAllowances estimate={estimate} itemsByCode={itemsByCode} />
      <UnbudgetedRisks estimate={estimate} itemsByCode={itemsByCode} />
    </section>
  );
}

/**
 * W6-01 (D-W6-01-01, rule R0): a composition row appears only for a basis that has a
 * contributor, so a `€ 0.00` never stands in for "not recorded" or "no allowance".
 */
function hasBasis(forwardPlan: ForwardPlanBlock, basis: ForwardContributorBasis): boolean {
  return forwardPlan.contributors.some((row) => row.basis === basis);
}

/**
 * W6-01: the "What to set aside from here" tier exists only once the person has recorded
 * something - an actual, a settled item, an exclusion or a received benefit. Before that it is
 * just the estimate again, headed "Updated with what you’ve recorded".
 */
function hasRecordedRecords(forwardPlan: ForwardPlanBlock): boolean {
  return (
    forwardPlan.contributors.some((row) => row.basis === "actual")
    || forwardPlan.settled.length > 0
    || forwardPlan.excluded.length > 0
    || forwardPlan.benefits_recorded.length > 0
  );
}

function ForwardPlanTier({
  forwardPlan,
  itemsByCode,
}: {
  forwardPlan: ForwardPlanBlock;
  itemsByCode: ReadonlyMap<string, PlanItem>;
}) {
  const stillContributing = forwardPlan.settled.filter((row) => row.still_contributes);
  const namesFor = (codes: string[]) =>
    codes.map((code) => itemsByCode.get(code)?.english_name ?? code).join(", ");

  return (
    <div
      className="plan-summary__tier plan-summary__tier--forward"
      aria-labelledby="plan-summary-forward-title"
    >
      <h3 className="eyebrow" id="plan-summary-forward-title" tabIndex={-1}>
        What to set aside from here <StatusChip tone="neutral">Includes your records</StatusChip>
      </h3>
      <p className="plan-summary__tier-intro">
        Updated with what you’ve recorded. Your original estimate is unchanged.
      </p>
      <div className="plan-summary__tier-body">
        <section className="calculation">
          <p className="eyebrow">Set aside</p>
          <h2>
            <span>{formatAmountOrRange(forwardPlan.monthly_set_aside)}</span> per month
          </h2>
          <div className="calculation__sum">
            {forwardPlan.contributors.map((row) => (
              <p key={row.item_code}>
                <span>{itemsByCode.get(row.item_code)?.english_name ?? row.item_code}</span>
                <strong>
                  {row.monthly
                    ? formatCurrency(row.monthly)
                    : `${formatCurrency(row.monthly_min as string)}–${formatCurrency(row.monthly_max as string)}`}
                </strong>
              </p>
            ))}
            {hasBasis(forwardPlan, "actual") && (
              <p className="calculation__subtotal">
                <span>From what you’ve recorded</span>
                <strong>{formatAmountOrRange(forwardPlan.composition.from_actuals)}</strong>
              </p>
            )}
            {hasBasis(forwardPlan, "estimate") && (
              <p className="calculation__subtotal">
                <span>From our estimates</span>
                <strong>{formatAmountOrRange(forwardPlan.composition.from_estimates)}</strong>
              </p>
            )}
            {hasBasis(forwardPlan, "planning_allowance") && (
              <p className="calculation__subtotal">
                <span>From planning allowances</span>
                <strong>{formatAmountOrRange(forwardPlan.composition.from_planning_allowances)}</strong>
              </p>
            )}
          </div>
          {forwardPlan.excluded.length > 0 && (
            <p>
              No longer counted, because you’ve marked{" "}
              {forwardPlan.excluded.length === 1 ? "it" : "them"} done:{" "}
              {namesFor(forwardPlan.excluded.map((row) => row.item_code))}.
            </p>
          )}
          {stillContributing.length > 0 && (
            <p>
              Still counted, because we can’t confirm{" "}
              {stillContributing.length === 1 ? "it" : "they"} won’t come up again this year:{" "}
              {namesFor(stillContributing.map((row) => row.item_code))}.
            </p>
          )}
          {forwardPlan.benefits_recorded.length > 0 && (
            <p>Money you receive is never subtracted from what you set aside.</p>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * W6-01 (D-W6-01-01, rule R0): a money figure is displayed only when at least one visible item
 * contributes to it; otherwise the position is stated in words. The payload's structural
 * `"0.00"` for an empty `reconciles_to` is not a figure and is never rendered.
 */
function emptyTotalCopy(kind: "obligation" | "benefit", count: number): { heading: string; body?: string } {
  if (kind === "obligation") {
    if (count === 0) return { heading: "No payments identified from your answers." };
    return {
      heading: "We can’t total these payments yet",
      body:
        count === 1
          ? "The payment we found doesn’t have a reviewed amount yet, so there is nothing trustworthy to add up. It says below what to check."
          : `None of the ${count} payments we found has a reviewed amount yet, so there is nothing trustworthy to add up. Each one below says what to check.`,
    };
  }
  if (count === 0) return { heading: "No benefits identified from your answers." };
  return {
    heading: "We can’t total money you may receive yet",
    body:
      count === 1
        ? "We haven’t calculated the benefit we found. It says below how to check what you could get."
        : `We haven’t calculated any of the ${count} benefits we found. Each one below says how to check what you could get.`,
  };
}

function Aggregate({ title, value, contributors, excluded, kind }: { title: string; value: MoneyAggregate; contributors: PlanItem[]; excluded: number; kind: "obligation" | "benefit" }) {
  if (contributors.length === 0 || value.reconciles_to.length === 0) {
    const copy = emptyTotalCopy(kind, excluded);
    return <section className="calculation"><p className="eyebrow">{title}</p><h2>{copy.heading}</h2>
      {copy.body && <p>{copy.body}</p>}
    </section>;
  }
  const display = value.amount ? formatCurrency(value.amount) : `${formatCurrency(value.min as string)}–${formatCurrency(value.max as string)}`;
  return <section className="calculation"><p className="eyebrow">{title}</p><h2>{display}</h2>
    <p>Includes {contributors.map((item) => item.english_name).join(", ")}.</p>
    {excluded > 0 && <p>{excluded} identified {excluded === 1 ? "item is" : "items are"} not included because no trustworthy amount is available yet.</p>}
  </section>;
}

/**
 * W4-04: summary slot 4, alongside the totals and the reserve. `null`
 * (`timeline` absent) renders nothing — the summary reads exactly as it
 * did before this story. `next: null` is a normal, honest state, not an
 * error. See docs/design/features/W4-04.md "What's coming next".
 */
function NextUp({
  timeline,
  itemsByCode,
  actionCount = 0,
}: {
  timeline: TimelineBlock | null;
  itemsByCode: ReadonlyMap<string, PlanItem>;
  /** W6-02 U2 (D-W6-02-02): how many "Things to do" the plan holds. */
  actionCount?: number;
}) {
  if (!timeline) return null;
  if (!timeline.next) {
    return (
      <section className="calculation calculation--next">
        <p className="eyebrow">What’s coming next</p>
        <p>We can’t tell you what’s next yet. Nothing in your plan has a date we’d stand behind.</p>
        {/* W6-02 U2 (D-W6-02-02): with no dated next item, point at the steps that exist,
            without implying an order, a date or urgency. Nothing is added when there are none. */}
        {actionCount > 0 && (
          <p>
            Start with <a href="#section-actions-title">Things to do</a> — {actionCount}{" "}
            {actionCount === 1 ? "step" : "steps"} that may apply to you.
          </p>
        )}
      </section>
    );
  }
  const item = itemsByCode.get(timeline.next.item_code);
  if (!item || !item.timing) return null;
  return (
    <section className="calculation calculation--next">
      <p className="eyebrow">What’s coming next</p>
      <p>
        Next up: <a href={`#item-${item.code}`}>{item.english_name}</a> — {formatTiming(item.timing)}
      </p>
    </section>
  );
}

function monthHeading(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  return new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "Europe/Amsterdam",
  }).format(new Date(Date.UTC(year, month - 1, 1, 12)));
}

function TimelineItemList({
  codes,
  itemsByCode,
  showTiming = true,
}: {
  codes: string[];
  itemsByCode: ReadonlyMap<string, PlanItem>;
  showTiming?: boolean;
}) {
  return (
    <ul>
      {codes.map((code) => {
        const item = itemsByCode.get(code);
        if (!item) return null;
        return (
          <li key={code}>
            <a href={`#item-${code}`}>{item.english_name}</a>
            {showTiming && item.timing && (
              <span className="timeline__item-timing"> — {formatTiming(item.timing)}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * W4-04: the chronological plan. `null` (`timeline` absent) renders
 * nothing — no stub, no explanation. An index into the four kind sections
 * above, which stay the primary way items are read; every row here links
 * back to its card. See docs/design/features/W4-04.md
 * "The chronological section".
 */
function WhenThingsHappen({
  estimate,
  timeline,
  itemsByCode,
}: {
  estimate: EstimatePreview;
  timeline: TimelineBlock | null;
  itemsByCode: ReadonlyMap<string, PlanItem>;
}) {
  if (!timeline) return null;

  // W6-02 U1 (D-W6-02-02): when no item sits in any month, "already passed" or "further
  // ahead", twelve "Nothing this month" rows would state an absence the product cannot
  // know. Say so once, with the approved W4-04 sentence, and list only the groups that
  // carry the items. As soon as any item is placed, all twelve months render as before.
  const nothingPlaced =
    timeline.overdue.item_codes.length === 0
    && timeline.beyond_plan_period.item_codes.length === 0
    && timeline.months.every((month) => month.item_codes.length === 0);

  return (
    <section className="timeline" aria-labelledby="timeline-title">
      <div>
        <p className="eyebrow">Your plan over time</p>
        <h2 id="timeline-title" tabIndex={-1}>When things happen</h2>
        <p>
          Everything below is placed using your plan period, {formatDate(estimate.estimate_period.start)}{" "}
          to {formatDate(estimate.estimate_period.end)}. Times shown are Amsterdam local dates.
        </p>
        {nothingPlaced && <p>We couldn’t place anything in time yet — here’s why, item by item.</p>}
      </div>

      {timeline.overdue.item_codes.length > 0 && (
        <div className="timeline__group">
          <h3>Already passed</h3>
          <p className="timeline__group-intro">
            These dates have gone by. It may still be worth checking each one.
          </p>
          <TimelineItemList codes={timeline.overdue.item_codes} itemsByCode={itemsByCode} />
        </div>
      )}

      {!nothingPlaced && timeline.months.map((month) => (
        <div className="timeline__group" key={month.month}>
          <h3>{monthHeading(month.month)}</h3>
          {month.item_codes.length === 0 ? (
            <p className="timeline__empty">Nothing this month</p>
          ) : (
            <TimelineItemList codes={month.item_codes} itemsByCode={itemsByCode} />
          )}
        </div>
      ))}

      {timeline.beyond_plan_period.item_codes.length > 0 && (
        <div className="timeline__group">
          <h3>Further ahead than this plan covers</h3>
          <TimelineItemList codes={timeline.beyond_plan_period.item_codes} itemsByCode={itemsByCode} />
        </div>
      )}

      {timeline.when_triggered.item_codes.length > 0 && (
        <div className="timeline__group">
          <h3>When you trigger them</h3>
          <p className="timeline__group-intro">
            These start when something happens. We’ll place them once we know the date.
          </p>
          <TimelineItemList codes={timeline.when_triggered.item_codes} itemsByCode={itemsByCode} />
        </div>
      )}

      {timeline.unknown.item_codes.length > 0 && (
        <div className="timeline__group">
          <h3>We can’t place these yet</h3>
          <p className="timeline__group-intro">
            We don’t have a date or a window we’d stand behind for these.
          </p>
          <TimelineItemList codes={timeline.unknown.item_codes} itemsByCode={itemsByCode} showTiming={false} />
        </div>
      )}
    </section>
  );
}

function Section({
  section,
  items,
  onImprove,
  planning,
  onSaveDeadline,
  onRemoveDeadline,
  onSetItemReminder,
  online,
}: {
  section: (typeof SECTIONS)[number];
  items: PlanItem[];
  onImprove?: (item: PlanItem) => void;
  planning: PlanningBlock | null;
  onSaveDeadline?: (itemCode: string, deadline: string) => Promise<void>;
  onRemoveDeadline?: (itemCode: string) => Promise<void>;
  onSetItemReminder?: (itemCode: string, enabled: boolean) => Promise<void>;
  online: boolean;
}) {
  return (
    <section aria-labelledby={`${section.id}-title`}>
      <div className="section-heading">
        <div>
          <h2 id={`${section.id}-title`} tabIndex={-1}>
            {section.heading} ({items.length})
          </h2>
          <p>{section.intro}</p>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="section-empty">{section.empty}</p>
      ) : (
        <div className="estimate-grid">
          {items.map((item) => (
            <PlanItemCard
              key={item.code}
              item={item}
              onImprove={onImprove}
              planning={planning}
              editableTiming={Boolean(onSaveDeadline)}
              onSaveDeadline={onSaveDeadline}
              onRemoveDeadline={onRemoveDeadline}
              onSetReminder={onSetItemReminder}
              online={online}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export function EstimateView({
  estimate,
  onEdit,
  saved = false,
  onImprove,
  onSaveDeadline,
  onRemoveDeadline,
  onSetItemReminder,
  online = true,
  focusItemCode,
  onFocusHandled,
  changeSummary,
  savePrompt,
  feedback,
}: {
  estimate: EstimatePreview;
  onEdit?: () => void;
  saved?: boolean;
  onImprove?: (item?: PlanItem) => void;
  /** W4-04: present only from a saved plan — see `SavedPlanPage`. Their
   * mere presence is what gates the deadline control on; a preview never
   * passes them and never shows the control. See
   * docs/architecture/features/W4-04.md section 6 and
   * docs/design/features/W4-04.md, "A user's own date". */
  onSaveDeadline?: (itemCode: string, deadline: string) => Promise<void>;
  onRemoveDeadline?: (itemCode: string) => Promise<void>;
  /** W5-02: present only from a saved plan, mirroring `onSaveDeadline`
   * exactly — a preview never passes this and never shows the per-item
   * reminder line. See docs/architecture/features/W5-02.md §9. */
  onSetItemReminder?: (itemCode: string, enabled: boolean) => Promise<void>;
  /** W5-02: whether the browser currently has a connection — gates every
   * reminder toggle's `disabled` state and hint. Defaults to `true` so
   * every caller that predates this story (there is only one: previews
   * never pass `onSetItemReminder`, so this has nothing to gate for them
   * anyway) renders unchanged. See docs/design/features/W5-02.md,
   * "States". */
  online?: boolean;
  /**
   * W4-05: the item code whose detail view was just closed, if any — see
   * `useRouter`'s `justClosedItemCode`. Present only for the one render
   * right after returning from a detail view; focuses that item's card
   * once, then calls `onFocusHandled` so the signal isn't reused on a
   * later, unrelated visit to this same plan. See
   * docs/design/features/W4-05.md "Responsive/accessibility".
   */
  focusItemCode?: string;
  onFocusHandled?: () => void;
  /**
   * W4-07: the profile-edit "what changed" summary, already rendered by
   * the caller (`ProfileChangeSummary`, since building it needs
   * `estimate.items`, which the caller already holds). `undefined`/`null`
   * renders nothing — the ordinary case on every visit that did not just
   * follow a save. See docs/architecture/features/W4-07.md §7.
   */
  changeSummary?: ReactNode;
  /** Preview-only save prompt, placed immediately before the summary. */
  savePrompt?: ReactNode;
  /**
   * W4-08: the feedback panel, already constructed by the caller (which
   * knows the surface and access context) — the same "caller builds it,
   * this component only places it" division `changeSummary` above already
   * uses. `undefined` renders nothing and adds no "Feedback" entry to the
   * contents nav below; every real caller passes one. See
   * docs/architecture/features/W4-08.md §4.
   */
  feedback?: ReactNode;
}) {
  // W4-05: this hook must run on every render, including the boundary-card
  // path below — React's Rules of Hooks forbid calling a hook only on
  // some renders. Placed first, before the validity check, for exactly
  // that reason.
  useEffect(() => {
    if (!focusItemCode) return;
    document.getElementById(`item-${focusItemCode}`)?.focus();
    onFocusHandled?.();
    // Runs once per mount only — `EstimateView` is freshly mounted every
    // time the plan reappears after a detail view closes (see
    // `ResultPage`/`SavedPlanPage`), so "on mount" is exactly "just
    // returned from detail".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This boundary is for a structurally broken payload only. A plan with many
  // uncalculated items, an unfamiliar code, or more than two items is normal
  // and must never land here.
  if (!isValidEstimatePayload(estimate)) {
    return (
      <section className="boundary-card" role="alert">
        <StatusChip tone="error">Overview unavailable</StatusChip>
        <h1>We couldn’t prepare a trustworthy overview right now</h1>
        <p>
          Some information we rely on is missing, so we are not showing partial figures. We will
          not substitute a guessed amount.
        </p>
        {onEdit && (
          <Button variant="secondary" onClick={onEdit}>
            Review answers
          </Button>
        )}
      </section>
    );
  }

  const calculatedCount = estimate.items.filter(
    (item) => item.calculation_state === "calculated",
  ).length;
  const planningBlock = planningBlockIfValid(estimate);
  const timeline = timelineIfValid(estimate);
  const validForwardPlan = forwardPlanIfValid(estimate);
  const forwardPlan = validForwardPlan && hasRecordedRecords(validForwardPlan) ? validForwardPlan : null;
  const hasAnyAllowance = Boolean(
    planningBlock
    && (planningBlock.allowances.length > 0 || planningBlock.one_time_allowances.length > 0),
  );
  // W6-02 U3: whether anything contributes to the monthly reserve at all.
  const hasReservedContributor = estimate.items.some((item) => item.included_in_reserve);
  const actionCount = estimate.items.filter((item) => item.kind === "action").length;
  // W4-05: built once per render and threaded through every child that
  // previously ran its own `estimate.items.find(...)` — a plan is tens of
  // items, not thousands, but the summary, the timeline and every section
  // together could otherwise re-scan the array dozens of times per
  // render. See docs/architecture/features/W4-05.md "Scalability/performance".
  const itemsByCode = new Map(estimate.items.map((item) => [item.code, item] as const));

  return (
    <>
      {changeSummary}
      <section className="result-heading">
        <p className="eyebrow">{saved ? "Your saved overview" : "Your personalised overview"}</p>
        <StatusChip tone="success">{saved ? "Saved plan" : "Overview ready"}</StatusChip>
        <h1>What may apply to you in the Netherlands</h1>
        <p className="lede">
          Based on your answers, here’s what may apply to you. Some amounts we can estimate; for
          the rest we’ll show you what to check and where.
        </p>
        <div className="profile-summary">
          <p>
            {formatDate(estimate.estimate_period.start)} to{" "}
            {formatDate(estimate.estimate_period.end)}
          </p>
          {onEdit && (
            <Button variant="quiet" onClick={onEdit}>
              Edit your answers
            </Button>
          )}
        </div>
        <p className="sr-only">
          {estimate.items.length} items found, {calculatedCount} with estimated amounts.
        </p>
      </section>

      <nav aria-label="Plan contents" className="plan-contents">
        <p className="eyebrow">Plan contents</p>
        <ul>
          <li>
            <a href="#plan-summary-title" onClick={followPlanContentsLink}>Summary</a>
          </li>
          {!saved && savePrompt && (
            <li>
              <a href="#preview-save-plan" onClick={followPlanContentsLink}>Save this plan</a>
            </li>
          )}
          {onImprove && estimate.items.some((item) => item.calculation_state === "needs_more_information") && (
            <li>
              <a href="#improve-title" onClick={followPlanContentsLink}>Improve your estimates</a>
            </li>
          )}
          {forwardPlan && (
            <li>
              <a href="#plan-summary-forward-title" onClick={followPlanContentsLink}>What to set aside from here</a>
            </li>
          )}
          {timeline && (
            <li>
              <a href="#timeline-title" onClick={followPlanContentsLink}>When things happen</a>
            </li>
          )}
          {SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}-title`} onClick={followPlanContentsLink}>
                {section.heading} ({estimate.items.filter((item) => item.kind === section.kind).length})
              </a>
            </li>
          ))}
          {feedback && (
            <li>
              <a href="#feedback-title" onClick={followPlanContentsLink}>Feedback</a>
            </li>
          )}
          {saved && (
            <li>
              <a href="#manage-plan-title" onClick={followPlanContentsLink}>Manage this saved plan</a>
            </li>
          )}
        </ul>
      </nav>

      {!saved && savePrompt}

      <section className="plan-summary" aria-labelledby="plan-summary-title">
        <h2 id="plan-summary-title" tabIndex={-1}>Your plan at a glance</h2>

        <div className="plan-summary__tier" aria-labelledby="plan-summary-calculated-title">
          <p className="eyebrow" id="plan-summary-calculated-title">
            Calculated from reviewed official rates
          </p>
          <p className="plan-summary__tier-intro">
            These come from rates we’ve reviewed for your situation.
          </p>
          <div className="plan-summary__tier-body">
            <Aggregate title="12-month outgoing total" value={estimate.totals.outgoing_12_month}
              contributors={estimate.items.filter((item) => item.kind === "obligation" && item.included_in_total)}
              excluded={estimate.items.filter((item) => item.kind === "obligation" && !item.included_in_total).length}
              kind="obligation" />
            <Aggregate title="Potential benefits" value={estimate.totals.potential_benefits}
              contributors={estimate.items.filter((item) => item.kind === "benefit" && item.included_in_total)}
              excluded={estimate.items.filter((item) => item.kind === "benefit" && !item.included_in_total).length}
              kind="benefit" />
          </div>
        </div>

        <div className="plan-summary__tier" aria-labelledby="plan-summary-planning-title">
          <p className="eyebrow" id="plan-summary-planning-title">
            Planning recommendation <StatusChip tone="neutral">Not a bill</StatusChip>
          </p>
          {/* W6-02 U3: the intro describes what the figure below actually is. */}
          {hasAnyAllowance ? (
            <p className="plan-summary__tier-intro">
              Amounts to set aside for costs we can’t calculate exactly yet. These are not
              assessments and not amounts you have been charged.
            </p>
          ) : hasReservedContributor ? (
            <p className="plan-summary__tier-intro">
              What to set aside each month for the payments we could estimate from reviewed
              official rates. This is not a bill or an amount you have been charged.
            </p>
          ) : null}
          <RecommendedBuffer estimate={estimate} itemsByCode={itemsByCode} />
        </div>

        {forwardPlan && <ForwardPlanTier forwardPlan={forwardPlan} itemsByCode={itemsByCode} />}

        <NextUp timeline={timeline} itemsByCode={itemsByCode} actionCount={actionCount} />
      </section>

      {onImprove && estimate.items.some((item) => item.calculation_state === "needs_more_information") && (
        <section className="management" aria-labelledby="improve-title">
          <div><p className="eyebrow">More precise estimates</p><h2 id="improve-title">One answer may improve more than one estimate</h2></div>
          <Button onClick={() => onImprove()}>Improve my estimates</Button>
        </section>
      )}

      <WhenThingsHappen estimate={estimate} timeline={timeline} itemsByCode={itemsByCode} />

      {SECTIONS.map((section) => (
        <Section
          key={section.id}
          section={section}
          items={estimate.items.filter((item) => item.kind === section.kind)}
          onImprove={onImprove ? (item) => onImprove(item) : undefined}
          planning={planningBlock}
          onSaveDeadline={onSaveDeadline}
          onRemoveDeadline={onRemoveDeadline}
          onSetItemReminder={onSetItemReminder}
          online={online}
        />
      ))}

      <details className="assumption-panel">
        <summary>What this overview assumes</summary>
        <ul>
          <li>The reviewed rules apply for the displayed estimate period.</li>
          <li>Only items shown as estimates are added to your monthly reserve.</li>
          <li>Official assessments may differ if your circumstances or the rates change.</li>
          <li>This is a budgeting overview, not a bill, tax advice or an eligibility decision.</li>
          {hasAnyAllowance && (
            <li>
              Planning allowances are conservative ranges for costs we can’t calculate for your
              situation yet. They are not assessments and not amounts you have been charged.
            </li>
          )}
        </ul>
      </details>

      {feedback && (
        <section className="plan-feedback" aria-labelledby="feedback-title">
          <h2 id="feedback-title" tabIndex={-1}>Feedback</h2>
          {feedback}
        </section>
      )}
    </>
  );
}







